import React, { useMemo, useRef, useState, useEffect } from 'react'

const LOG_WINDOW = 3
const TYPING_SPEED_MS = 22

export function MissionLog({ missionStages = [], artemisProgress = 0 }) {
  const allEntries = useMemo(() => {
    const entries = []
    missionStages.forEach((stage, idx) => {
      if (!stage.visible) return
      const num = (idx + 1).toString().padStart(2, '0')
      entries.push({
        id: stage.id,
        label: `${num} ${stage.shortName || stage.name}`,
        trajectoryPercent: stage.trajectoryPercent,
        type: 'stage',
      })
      ;(stage.subPoints ?? []).forEach((sp) => {
        if (!sp.visible) return
        const title = sp.lightbox?.title ?? sp.label
        entries.push({
          id: sp.id,
          label: `${sp.label} ${title.toUpperCase()}`,
          trajectoryPercent: sp.trajectoryPercent,
          type: 'subpoint',
        })
      })
    })
    return entries.sort((a, b) => a.trajectoryPercent - b.trajectoryPercent)
  }, [missionStages])

  const activeEntries = useMemo(
    () => allEntries.filter((e) => e.trajectoryPercent <= artemisProgress),
    [allEntries, artemisProgress]
  )

  const visible = activeEntries.slice(-LOG_WINDOW)

  // Typewriter state: which entry is typing and how many chars are revealed
  const [typingState, setTypingState] = useState(null) // { id, fullText, chars }
  const prevLastIdRef = useRef(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    const last = activeEntries[activeEntries.length - 1]
    if (!last || last.id === prevLastIdRef.current) return

    prevLastIdRef.current = last.id
    if (intervalRef.current) clearInterval(intervalRef.current)

    const prefix = last.type === 'subpoint' ? '  > ' : '> '
    const fullText = prefix + last.label

    setTypingState({ id: last.id, fullText, chars: 0 })

    intervalRef.current = setInterval(() => {
      setTypingState((prev) => {
        if (!prev) return null
        const next = prev.chars + 1
        if (next >= prev.fullText.length) {
          clearInterval(intervalRef.current)
          return null
        }
        return { ...prev, chars: next }
      })
    }, TYPING_SPEED_MS)
  }, [activeEntries])

  // Cleanup on unmount
  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current) }, [])

  return (
    <div className="mission-log">
      <div className="mission-log__header">{'[ LOG ]'}</div>
      <div className="mission-log__rule" />
      <div className="mission-log__body">
        {visible.map((entry, i) => {
          const isLast = i === visible.length - 1
          const isTyping = typingState?.id === entry.id
          const prefix = entry.type === 'subpoint' ? '  > ' : '> '
          const displayText = isTyping
            ? typingState.fullText.slice(0, typingState.chars)
            : prefix + entry.label

          return (
            <div
              key={entry.id}
              className={`mission-log__entry mission-log__entry--${entry.type}`}
            >
              {displayText}{isLast && <span className="mission-log__cursor">{'█'}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default MissionLog
